import React, { useState } from 'react'

import {
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import { Add, Delete, Edit, Email, Sms, People } from '@material-ui/icons'

import { Container } from '../../components'
import {
    ConcessionMessage,
    ConcessionMessageRecipient,
} from '../../models/Concession'
import {
    fetchMessageRecipients,
    useConcessionMessages,
} from '../../hooks/useConcessionMessages'
import { ConcessionMessageForm } from './ConcessionMessageForm'
import { displayDateTime, messageStatusLabel } from './utils'
import { useConcessionStyles } from './styles'

interface Props {
    concessionId: number
    lifetimeStart: string
    lifetimeEnd: string
}

export const ConcessionMessagesTab: React.FC<Props> = ({
    concessionId,
    lifetimeStart,
    lifetimeEnd,
}) => {
    const theme = useTheme()
    const classes = useConcessionStyles()
    const { messages, isLoading, deleteMessage } = useConcessionMessages(
        concessionId,
    )

    const [showForm, setShowForm] = useState(false)
    const [editing, setEditing] = useState<ConcessionMessage | undefined>()

    // Recipients preview state, keyed by message id.
    const [recipientsFor, setRecipientsFor] = useState<number | null>(null)
    const [recipients, setRecipients] = useState<ConcessionMessageRecipient[]>(
        [],
    )
    const [recipientsCount, setRecipientsCount] = useState(0)
    const [loadingRecipients, setLoadingRecipients] = useState(false)

    const openNew = () => {
        setEditing(undefined)
        setShowForm(true)
    }

    const openEdit = (message: ConcessionMessage) => {
        setEditing(message)
        setShowForm(true)
    }

    const closeForm = () => {
        setShowForm(false)
        setEditing(undefined)
    }

    const previewRecipients = async (message: ConcessionMessage) => {
        if (recipientsFor === message.id) {
            setRecipientsFor(null)
            return
        }
        setRecipientsFor(message.id)
        setLoadingRecipients(true)
        try {
            const res = await fetchMessageRecipients(message.id)
            setRecipients(res.recipients)
            setRecipientsCount(res.count)
        } catch {
            setRecipients([])
            setRecipientsCount(0)
        } finally {
            setLoadingRecipients(false)
        }
    }

    if (showForm) {
        return (
            <div className={classes.body}>
                <div className={classes.section}>
                    <Typography
                        variant="subtitle1"
                        style={{
                            fontWeight: 700,
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        {editing ? 'Edit message' : 'New message'}
                    </Typography>
                    <ConcessionMessageForm
                        concessionId={concessionId}
                        lifetimeStart={lifetimeStart}
                        lifetimeEnd={lifetimeEnd}
                        message={editing}
                        onClose={closeForm}
                    />
                </div>
            </div>
        )
    }

    return (
        <div className={classes.body}>
            <Container
                alignItems="center"
                style={{ marginBottom: theme.spacing(2) }}
            >
                <Container direction="column" flex={1}>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                        Scheduled messages
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        Sent automatically to eligible, non-renewed tenants.
                    </Typography>
                </Container>
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<Add />}
                    onClick={openNew}
                    style={{ textTransform: 'none' }}
                >
                    Add message
                </Button>
            </Container>

            {isLoading ? (
                <Container justifyContent="center" style={{ padding: 24 }}>
                    <CircularProgress size={24} />
                </Container>
            ) : messages.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                    No messages scheduled yet.
                </Typography>
            ) : (
                messages.map((message) => (
                    <Paper
                        key={message.id}
                        variant="outlined"
                        style={{
                            padding: theme.spacing(1.5),
                            marginBottom: theme.spacing(1.5),
                        }}
                    >
                        <Container alignItems="flex-start">
                            <Container
                                alignItems="center"
                                style={{
                                    gap: theme.spacing(1),
                                    marginRight: theme.spacing(1),
                                }}
                            >
                                {message.message_type === 'email' ? (
                                    <Email fontSize="small" color="action" />
                                ) : (
                                    <Sms fontSize="small" color="action" />
                                )}
                            </Container>
                            <Container direction="column" flex={1}>
                                {message.subject && (
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 600 }}
                                    >
                                        {message.subject}
                                    </Typography>
                                )}
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    style={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {message.content}
                                </Typography>
                                <Container
                                    alignItems="center"
                                    style={{
                                        gap: theme.spacing(1),
                                        marginTop: theme.spacing(0.5),
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        {displayDateTime(message.send_at)}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={messageStatusLabel(
                                            message.status,
                                        )}
                                    />
                                </Container>
                            </Container>
                            <Container style={{ gap: theme.spacing(0.5) }}>
                                <Tooltip title="Preview recipients">
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            previewRecipients(message)
                                        }
                                    >
                                        <People fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit message">
                                    <IconButton
                                        size="small"
                                        onClick={() => openEdit(message)}
                                        disabled={message.status === 'sent'}
                                    >
                                        <Edit fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete message">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            if (
                                                window.confirm(
                                                    'Delete this scheduled message?',
                                                )
                                            ) {
                                                deleteMessage.mutate(message.id)
                                            }
                                        }}
                                    >
                                        <Delete
                                            fontSize="small"
                                            color="error"
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Container>
                        </Container>

                        {recipientsFor === message.id && (
                            <>
                                <Divider
                                    style={{ margin: theme.spacing(1.5, 0) }}
                                />
                                {loadingRecipients ? (
                                    <Container
                                        justifyContent="center"
                                        style={{ padding: 8 }}
                                    >
                                        <CircularProgress size={18} />
                                    </Container>
                                ) : (
                                    <Container direction="column">
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            style={{
                                                marginBottom: theme.spacing(
                                                    0.5,
                                                ),
                                            }}
                                        >
                                            {recipientsCount} eligible recipient
                                            {recipientsCount === 1 ? '' : 's'}
                                        </Typography>
                                        {recipients.length === 0 ? (
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                No eligible recipients right
                                                now.
                                            </Typography>
                                        ) : (
                                            recipients.map((r) => (
                                                <Typography
                                                    key={r.lease}
                                                    variant="body2"
                                                >
                                                    {r.tenant_name}
                                                    {r.tenant_email
                                                        ? ` — ${r.tenant_email}`
                                                        : ''}
                                                </Typography>
                                            ))
                                        )}
                                    </Container>
                                )}
                            </>
                        )}
                    </Paper>
                ))
            )}
        </div>
    )
}
